import React, { useState, useEffect, useCallback, useRef } from "react";
import clsx from "clsx";
import { Dialog, DialogContent } from "@mui/material";
import { BarcodeScanner, GradientColorHeader } from '@curves/ui-shared';
import { isFunction } from "@util/utils";

/**
 * 條碼掃描彈窗組件
 * 封裝條碼掃描器，並提供手動輸入選項
 *
 * @param open - 是否開啟彈窗
 * @param onClose - 關閉彈窗回調
 * @param onScanSuccess - 掃描成功回調
 * @param onScanError - 掃描錯誤回調
 * @param title - 彈窗標題
 * @param supportedFormats - 支援的條碼格式
 * @param continuousScan - 是否連續掃描模式
 * @returns {JSX.Element}
 * @constructor
 */
const BarcodeScannerDialog = ({
	open = false,
	onClose: onCloseProps,
	onScanSuccess,
	onScanError = () => {},
	title = "條碼掃描",
	supportedFormats,
	continuousScan = false,
}) => {
	const [isBarCodeClosing, setIsBarCodeClosing] = useState(false);

	const timeoutRef = useRef(null);

	// 處理掃描器成功或手動輸入提交
	const handleSuccess = useCallback(
		async decodedText => {
			if (isBarCodeClosing) return;

			// 將成功結果傳給外部回調，並等待結果
			let shouldClose = true;
			if (isFunction(onScanSuccess)) {
				const result = await onScanSuccess(decodedText);
				// 如果 onScanSuccess 返回 false，表示處理失敗，不關閉對話框
				if (result === false) {
					shouldClose = false;
				}
			}

			// 只有在成功且不是連續掃描模式時，才關閉彈窗
			if (shouldClose && !continuousScan) {
				onCloseProps();
			}
		},
		[isBarCodeClosing, onScanSuccess, continuousScan, onCloseProps]
	);

	const handleClose = useCallback(
		() => {
			// 1. 清除任何正在運行的舊計時器
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			setIsBarCodeClosing(true);
			onCloseProps();

			// 2. 啟動延遲重置，並將 ID 存入 ref，避免記憶體洩漏風險
			timeoutRef.current = setTimeout(() => {
				setIsBarCodeClosing(false);
				timeoutRef.current = null; // 重置 ref 標記
			}, 500);
		},
		// eslint-disable-next-line
		[onCloseProps]
	);

	// 確保元件卸載時清除計時器
	useEffect(
		() => {
			return () => {
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}
			};
		},
		// eslint-disable-next-line
		[]
	);

	// 當彈窗狀態改變時，統一重置所有狀態
	useEffect(
		() => {
			if (!open) {
				// 確保在外部控制關閉時，延遲重置也被取消
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
					timeoutRef.current = null;
				}
				setIsBarCodeClosing(false);
			}
		},
		// eslint-disable-next-line
		[open]
	);

	return (
		<Dialog className={clsx("simple-content", "dialog")} PaperProps={{ className: "wx-65rem" }} open={open} fullWidth>
			<GradientColorHeader onClose={handleClose}>{title}</GradientColorHeader>
			<DialogContent className="p-4">
				{open && !isBarCodeClosing && (
					<BarcodeScanner onScanSuccess={handleSuccess} onScanError={onScanError} title={title} supportedFormats={supportedFormats} hasButton continuousScan={continuousScan} />
				)}
			</DialogContent>
		</Dialog>
	);
};

export default BarcodeScannerDialog;
